import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ESTILO_PADRAO, ESTILOS, FONTE_DO_ESTILO, type Estilo } from '../nucleo/estilos';
import { pilhaFonte, registrarFonte, resolverFonte } from '../nucleo/fontes';
import { useSessaoOpcional } from '../nucleo/sessao';

/** Botão de escolha (rádio) no mesmo padrão do seletor de acento de Configurações > Aparência. */
export function OpcaoRadio({ marcado, aoEscolher, children, estilo, desabilitado }: { marcado: boolean; aoEscolher: () => void; children: ReactNode; estilo?: CSSProperties; desabilitado?: boolean }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={marcado}
      disabled={desabilitado}
      onClick={aoEscolher}
      style={estilo}
      className={`flex min-h-10 w-full min-w-0 items-center gap-2 rounded-lg border-2 px-3 py-2 text-left text-sm disabled:opacity-60 ${marcado ? 'border-acento-forte bg-acento-suave text-tinta-1' : 'border-tinta-3 text-tinta-2 hover:bg-sup-2'}`}
    >
      {children}
    </button>
  );
}

const fonteDe = (e: Estilo): CSSProperties | undefined => {
  const f = FONTE_DO_ESTILO[e];
  return f ? { fontFamily: pilhaFonte(resolverFonte(f).familia) } : undefined;
};

/**
 * Seletor de estilo de interface (27/10; N5, 28/10): as amostras e os nomes de Minha conta, usados também na aparência do
 * workspace e na fixação pelo administrador. Com `padrao`, a primeira opção é "seguir o padrão" (valor null), mostrando a
 * amostra do estilo que ele resolve.
 */
export function SeletorEstilo({
  valor,
  aoEscolher,
  desabilitado,
  rotulo,
  padrao,
  rotuloPadrao,
  nomeDoPadrao = true,
  compacto = false,
}: {
  valor: Estilo | null;
  aoEscolher: (estilo: Estilo | null) => void;
  desabilitado?: boolean;
  rotulo: string;
  /** Presente = oferece "seguir o padrão" (null); o valor é o estilo em que ele resolve. */
  padrao?: Estilo;
  rotuloPadrao?: string;
  /** Mostra, sob o rótulo de "seguir o padrão", o nome do estilo em que ele resolve (falso onde o padrão não é um estilo só). */
  nomeDoPadrao?: boolean;
  /** rcrm4 VIS-B4: dentro de modal ou gaveta (largura do painel, não da janela): uma coluna só, para os nomes não transbordarem. */
  compacto?: boolean;
}) {
  const { t } = useTranslation();
  // Modelo próprio (03/11): só aparece como opção quando o workspace tem um modelo em uso.
  const temModelo = !!useSessaoOpcional()?.aparencia?.modelo_ativo;
  const opcoes = ESTILOS.filter((e) => e !== 'proprio' || temModelo || valor === 'proprio');
  useEffect(() => {
    for (const e of ESTILOS) {
      const f = FONTE_DO_ESTILO[e];
      if (f) void registrarFonte(resolverFonte(f));
    }
  }, []);
  const amostra = (e: Estilo) => (
    <span data-amostra-estilo={e} aria-hidden className="flex h-9 w-14 shrink-0 items-end gap-1 rounded-md border border-borda p-1">
      <span className="amostra-cartao h-full flex-1 rounded-sm" />
      <span className="amostra-acento h-3 w-3 rounded-full" />
    </span>
  );
  return (
    <div role="radiogroup" aria-label={rotulo} className={`grid gap-2 ${compacto ? '' : 'sm:grid-cols-2 xl:grid-cols-3'}`} data-estilo-interface data-compacto={compacto || undefined}>
      {padrao && (
        <OpcaoRadio marcado={valor === null} desabilitado={desabilitado} aoEscolher={() => valor !== null && aoEscolher(null)} estilo={fonteDe(padrao)}>
          {amostra(padrao)}
          <span className="flex min-w-0 flex-col" data-estilo-seguir-padrao="">
            <span className="font-medium break-words">{rotuloPadrao ?? t('conta.estilo.seguirPadrao')}</span>
            {nomeDoPadrao && <span className="text-xs text-tinta-3">{t(`conta.estilo.nomes.${padrao}`)}</span>}
          </span>
        </OpcaoRadio>
      )}
      {opcoes.map((e) => (
        <OpcaoRadio key={e} marcado={valor === e} desabilitado={desabilitado} aoEscolher={() => valor !== e && aoEscolher(e)} estilo={fonteDe(e)}>
          {amostra(e)}
          <span className="flex min-w-0 flex-col">
            <span className="font-medium break-words">{t(`conta.estilo.nomes.${e}`)}</span>
            {e === ESTILO_PADRAO && <span className="text-xs text-tinta-3">{t('conta.estilo.padrao')}</span>}
          </span>
        </OpcaoRadio>
      ))}
    </div>
  );
}
